import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SimpleGrid, Card, Stack, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime } from '../../../../lib/format';
import { CopyableId } from '../../../../components/CopyableId';
import { useT, type Translate } from '../../../../i18n/I18nProvider';
import '../../components/billing.css';

interface GatewayMeta {
  code: string;
  name: string;
  descriptionKey: string;
  webhook: string;
}

// The three payment gateways. Configuration status is pulled live from the
// integrations registry; the webhook path is where each provider's ITN lands.
const GATEWAYS: GatewayMeta[] = [
  {
    code: 'payfast',
    name: 'PayFast',
    descriptionKey: 'billing.itn.gateway.payfast',
    webhook: '/webhooks/payfast/itn'
  },
  {
    code: 'stripe',
    name: 'Stripe',
    descriptionKey: 'billing.itn.gateway.stripe',
    webhook: '/webhooks/stripe/itn'
  },
  {
    code: 'paystack',
    name: 'Paystack',
    descriptionKey: 'billing.itn.gateway.paystack',
    webhook: '/webhooks/paystack/itn'
  }
];

interface Props {
  rows: admin.Integration[];
  loading: boolean;
}

/**
 * The last connection test, as one sentence. The three outcomes used to be
 * short-circuited into the same line, so an unrecognised status rendered as
 * blank space followed by a date.
 */
function lastTestLine(t: Translate, row: admin.Integration): string | null {
  const when = row.last_tested_at ? formatDateTime(row.last_tested_at) : null;
  let outcome: string;
  if (row.last_test_status === 'ok') outcome = t('billing.itn.test.passed');
  else if (row.last_test_status === 'error') outcome = t('billing.itn.test.failed');
  else if (row.last_test_status === 'untested') outcome = t('billing.itn.test.untested');
  else return when;
  return when ? `${outcome} · ${when}` : outcome;
}

/** One line of the card's key-value block: label left, value right. */
function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Group justify="space-between" align="center" gap="md" wrap="nowrap" className="rr-billing-kv">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <span className="rr-billing-kv__value">{children}</span>
    </Group>
  );
}

export function ItnHealthCards({ rows, loading }: Props) {
  const t = useT();
  const byCode = new Map(rows.map((i) => [i.provider_code, i]));

  return (
    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
      {GATEWAYS.map((g) => {
        const row = byCode.get(g.code);
        return (
          <Card key={g.code} withBorder>
            <Stack gap="md">
              <Group gap="xs" align="center" wrap="nowrap">
                <Text role="subtitle" fw={700}>{g.name}</Text>
                {/* The provider code is what the integrations registry keys on,
                    so it sits beside the name rather than under it. */}
                <Badge size="xs" variant="light">{g.code}</Badge>
              </Group>

              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t(g.descriptionKey)}</Text>

              <Stack gap="xs">
                <Row label={t('billing.itn.statusLabel')}>
                  <ConfigStatus row={row} loading={loading} t={t} />
                </Row>

                <Stack gap="xs">
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.itn.webhookLabel')}</Text>
                  {/* The path exists to be pasted into the provider's dashboard,
                      so it is copyable rather than plain caption text. */}
                  <CopyableId value={g.webhook} label={t('billing.itn.copyWebhook')} chars={40} />
                </Stack>

                <Row label={t('billing.itn.lastTestLabel')}>
                  <Text role="caption">{(row && lastTestLine(t, row)) || t('billing.itn.noTest')}</Text>
                </Row>

                {row?.updated_at && (
                  <Row label={t('billing.itn.updatedLabel')}>
                    <Text role="caption">{formatDateTime(row.updated_at)}</Text>
                  </Row>
                )}
                {row?.updated_by && (
                  <Row label={t('billing.itn.updatedByLabel')}>
                    <Text role="caption">{row.updated_by}</Text>
                  </Row>
                )}
              </Stack>

              {row?.last_test_status === 'error' && row.last_test_message && (
                <Text role="caption" c="var(--rr-color-error)">{row.last_test_message}</Text>
              )}
              {row?.notes && <Text role="caption" c="var(--rr-color-text-muted)">{row.notes}</Text>}

              <Group justify="flex-end">
                <Link to="/settings/integrations" className="rr-billing-cardlink">
                  {t('billing.itn.configure')} <span aria-hidden>→</span>
                </Link>
              </Group>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}

function ConfigStatus({ row, loading, t }: { row: admin.Integration | undefined; loading: boolean; t: Translate }) {
  if (loading) return <StatusBadge status="pending">{t('billing.itn.config.checking')}</StatusBadge>;
  if (!row) return <StatusBadge status="inactive">{t('status.notConfigured')}</StatusBadge>;
  if (!row.enabled) return <StatusBadge status="inactive">{t('status.disabled')}</StatusBadge>;
  if (row.last_test_status === 'ok') return <StatusBadge status="healthy">{t('billing.itn.config.connected')}</StatusBadge>;
  if (row.last_test_status === 'error') return <StatusBadge status="warning">{t('billing.itn.config.needsAttention')}</StatusBadge>;
  return <StatusBadge status="pending">{t('status.enabled')}</StatusBadge>;
}
